import { FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store/store';
import {
  AddMmsiToFleetGroupRequest,
  AddMmsiToFleetGroupResponse,
  DeleteUserFleetRequest,
  DeleteUserFleetResponse,
  GetFleetGroupsResponse,
  RemoveFleetGroupAssignmentsRequest,
  RemoveFleetGroupAssignmentsResponse,
  UpdateFleetGroupListRequest,
  UpdateFleetGroupListResponse,
} from 'ais-facade-api/api';

import { PutUserFleetResponse, PutUserFleetRequest, PutUserFleetCsvResponse } from 'ais-facade-api/api';

import { BASE_URL } from 'app/utils/utils';
import i18next from 'i18next';
import { onQueryStartedErrorToast } from './apiErrorHandler';

type FleetT = {
  mmsiArray?: number[];
  user_id: string;
  data?: FormData;
  key?: string;
};
export const fleetApi = createApi({
  reducerPath: 'fleet',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL + '/facade/versions/1/',
    credentials: 'include',
    mode: 'cors',
    prepareHeaders: (headers: Headers, { getState }) => {
      const userInfo = (getState() as RootState).userSlice;
      headers.set('Accept-Language', i18next.resolvedLanguage ? i18next.resolvedLanguage : 'ru');

      if (userInfo?.auth && userInfo?.user) {
        headers.set('x-ais-user-csrf', userInfo.auth.value.csrfToken);
        headers.set('ais-user-csrf', userInfo.auth.value.csrfToken);
      }
      return headers;
    },
  }),
  tagTypes: ['Fleet'],
  endpoints: (build) => ({
    addToFleet: build.mutation<PutUserFleetResponse, PutUserFleetRequest>({
      query: ({ mmsiArray }) => ({
        url: `fleet`,
        method: 'PUT',
        body: {
          mmsiArray: mmsiArray,
        },
        cache: 'no-cache',
        overrideExisting: false,
        withCredentials: true,
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    removeFromFleet: build.mutation<DeleteUserFleetResponse, DeleteUserFleetRequest>({
      query: ({ mmsiArray }) => ({
        url: `fleet`,
        method: 'DELETE',
        body: {
          mmsiArray: mmsiArray,
        },
        cache: 'no-cache',
        overrideExisting: false,
        withCredentials: true,
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    addFleetFromFile: build.mutation<{ data: PutUserFleetCsvResponse; error: FetchBaseQueryError }, FleetT>({
      query: ({ data }) => ({
        url: `fleet/import/csv`,
        method: 'PUT',
        body: data,
        cache: 'no-cache',
        overrideExisting: false,
        withCredentials: true,
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getGroups: build.query<GetFleetGroupsResponse, void>({
      query: () => ({
        url: 'fleet/group',
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    updateGroups: build.mutation<UpdateFleetGroupListResponse, UpdateFleetGroupListRequest['values']>({
      query: (body) => ({
        url: `fleet/group-list`,
        method: 'PUT',
        body: {
          values: body,
        },
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    addVesselsToGroup: build.mutation<AddMmsiToFleetGroupResponse, { key: string; array: AddMmsiToFleetGroupRequest; }>({
      query: ({ key, array }) => ({
        url: `fleet/group/${key}/mmsi`,
        method: 'PUT',
        body: {
          mmsiArray: array.mmsiArray,
        },
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    removeGroupFromVessels: build.mutation<RemoveFleetGroupAssignmentsResponse, RemoveFleetGroupAssignmentsRequest>({
      query: ({ mmsiArray }) => ({
        url: `/fleet/group/assignments`,
        method: 'DELETE',
        body: {
          mmsiArray: mmsiArray,
        },
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
  }),
});

export const {
  useAddFleetFromFileMutation,
  useAddToFleetMutation,
  useAddVesselsToGroupMutation,
  useGetGroupsQuery,
  useRemoveFromFleetMutation,
  useRemoveGroupFromVesselsMutation,
  useUpdateGroupsMutation,
} = fleetApi;
